import React, { useEffect, useState } from 'react';
import { Link, useNotify, useRefresh } from 'react-admin';
import { clsx } from 'clsx';
import { notification } from 'antd';

import { axiosErrorToString, dateFormatter } from '@utils';
import { CalculateInvoiceResult, creditHttp } from '@network';
import { InitialPage } from '@components';
import {
  Credit,
  CreditInvoice,
  CreditInvoiceStatusType,
  OrderCompositionProduct,
  ProductOriginType,
} from '@types';
import { CreditInvoiceStatus } from '@pages/credit/CreditEdit/CreditInvoices/CreditInvoiceStatus';
import { CreditInvoiceNote } from '@pages/credit/CreditEdit/CreditInvoices/CreditInvoiceNotes';
import { CreditInvoiceUser } from '@pages/credit/CreditEdit/CreditInvoices/CreditInvoiceUser';
import { CreditInvoiceCompany } from '@pages/credit/CreditEdit/CreditInvoices/CreditInvoiceCompany';
import {
  CreditInvoiceProducts,
} from '@pages/credit/CreditEdit/CreditInvoices/CreditInvoiceProducts';
import cls from './credit-invoices.module.css';

const MAX_POSITIONS = 60;

const initialFormValues = {
  retailerName: '',
  zipState: '',
  xmlUuid: null,
  xmlDate: null,
  comment: '',
  status: CreditInvoiceStatusType.PENDING,
  discountAmount: null,
  isFinanceAnalysisApproved: null,
  isCxAnalysisApproved: null,
};

export interface CreditInvoiceFormValues {
  retailerName: string;
  zipState: string | null;
  xmlUuid: string | null;
  xmlDate: string | null;
  comment: string;
  status: CreditInvoiceStatusType;
  discountAmount: number | null;
  isFinanceAnalysisApproved?: boolean | null;
  isCxAnalysisApproved?: boolean | null;
}

interface CreditInvoiceDetailProps {
  credit: Credit;
  invoiceId: number;
}

export const CreditInvoicesDetail = ({ credit, invoiceId }: CreditInvoiceDetailProps) => {
  const refresh = useRefresh();
  const notify = useNotify();

  const [invoice, setInvoice] = useState<CreditInvoice | null>(null);
  const [formValues, setFormValues] = useState<CreditInvoiceFormValues>(initialFormValues);
  const [composition, setComposition] = useState<OrderCompositionProduct[]>([]);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [isrPercent, setIsrPercent] = useState<number | null>(null);
  const [iepsPercent, setIepsPercent] = useState<number | null>(null);
  const [calculate, setCalculate] = useState<CalculateInvoiceResult>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const date = invoice?.createdAt
    ? dateFormatter.toDateTime(invoice.createdAt, true) : '';
  const commentedDate = invoice?.commentedAt
    ? dateFormatter.toDateTime(invoice.commentedAt, true) : '';

  const updateValues = (e: any): void => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const updateStatus = (status: CreditInvoiceStatusType): void => {
    setFormValues({
      ...formValues,
      status,
    });
  };

  const calculateRequest = async () => {
    if (composition.length === 0) return;

    try {
      const nextCalculate = await creditHttp.calculateInvoice({
        composition: composition.map(position => ({
          ...position,
          externalShop: formValues.retailerName,
        })),
        discountAmount,
        isrPercent,
        iepsPercent,
      });
      setCalculate(nextCalculate);
    } catch (err: unknown) {
      console.error(err);
    }
  };

  useEffect(() => void calculateRequest(), [
    composition, discountAmount, isrPercent, iepsPercent,
  ]);

  const submit = async (creditId?: number | undefined) => {
    if (isSubmitting) {
      return;
    }

    const isDenied = formValues.status === CreditInvoiceStatusType.DENIED;
    setShowErrors(false);
    setIsSubmitting(true);

    if (!formValues.zipState && !isDenied) {
      notification.error({ message: 'ZIP Code is required', placement: 'bottom' });
      setIsSubmitting(false);
      return setShowErrors(true);
    }
    if (!formValues.retailerName && !isDenied) {
      notification.error({ message: 'Retailer Store is required', placement: 'bottom' });
      setIsSubmitting(false);
      return setShowErrors(true);
    }

    try {
      const isApproved
        = !!formValues.isFinanceAnalysisApproved && !!formValues.isCxAnalysisApproved;
      const newInvoice = await creditHttp.updateInvoice(invoiceId, {
        ...formValues,
        status: isApproved ? CreditInvoiceStatusType.APPROVED : formValues.status,
        composition: (composition || []).map(position => ({
          ...position,
          externalShop: formValues.retailerName,
        })),
        creditId: creditId ? creditId : credit.id,
        discountAmount,
        isrPercent,
        iepsPercent,
      });

      refresh();
      notification.success({ message: 'Saved', placement: 'bottom' });

      return newInvoice;
    } catch (e) {
      notify(axiosErrorToString(e));
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setFormValues({
      retailerName: invoice?.retailerName || '',
      status: invoice?.status as CreditInvoiceStatusType || CreditInvoiceStatusType.PENDING,
      comment: invoice?.comment || '',
      zipState: invoice?.zipState || '',
      xmlUuid: invoice?.xmlUuid || null,
      xmlDate: invoice?.xmlDate || null,
      discountAmount: invoice?.discountAmount ?? 0,
      isFinanceAnalysisApproved: invoice?.financeAnalysisStatus ?? null,
      isCxAnalysisApproved: invoice?.cxAnalysisStatus ?? null,
    });
    setDiscountAmount(invoice?.discountAmount ?? 0);
    setIsrPercent(invoice?.isrPercent ?? null);
    setIepsPercent(invoice?.iepsPercent ?? null);
  }, [invoice]);

  useEffect(() => {
    void (async () => {
      try {
        const loadedInvoice = await creditHttp.getInvoiceById(invoiceId);
        setInvoice(loadedInvoice);

        const isXml = loadedInvoice.isXml;
        const xmlProducts = loadedInvoice.xmlProducts || [];

        const nextComposition = loadedInvoice.positions.map((position, index) => {
          const invoicePosition: OrderCompositionProduct = {
            id: index,
            productId: position.product.id,
            qty: position.qty,
            productPack: position.productPack || null,
            productPrice: position.productPrice,
            taxes: position.taxes,
            isExternal: true,
            externalProduct: position.product.name,
            product: position.product,
          };
          if (isXml) {
            const [xmlName, xmlQty, xmlPrice] = (xmlProducts[index] || '').split('|');
            const qty = xmlQty ? +parseFloat(xmlQty).toFixed(3) : 1;
            const productPrice = xmlPrice ? +parseFloat(xmlPrice).toFixed(3) : 0;
            invoicePosition.qty = qty;
            invoicePosition.productPrice = productPrice;
            invoicePosition.xmlName = xmlName;
            invoicePosition.xmlQty = xmlQty;
            invoicePosition.xmlPrice = xmlPrice;
          }
          return invoicePosition;
        });

        if (isXml && xmlProducts.length > 0) {
          for (const xmlProduct of xmlProducts) {
            const [xmlName, xmlQty, xmlPrice] = (xmlProduct || '').split('|');
            const foundXmlPosition = nextComposition.find(pos => pos.xmlName === xmlName);
            if (!foundXmlPosition) {
              const qty = xmlQty ? +parseFloat(xmlQty).toFixed(3) : 1;
              const productPrice = xmlPrice ? +parseFloat(xmlPrice).toFixed(3) : 0;
              const invoicePosition: OrderCompositionProduct = {
                id: nextComposition.length,
                qty: qty,
                productPack: null,
                productPrice: productPrice,
                taxes: [],
                xmlName: xmlName,
                xmlQty: xmlQty,
                xmlPrice: xmlPrice,
                isExternal: true,
                productId: null,
              };
              nextComposition.push(invoicePosition);
            }
          }
        }

        setComposition(nextComposition);
      } catch (e) {
        notify(axiosErrorToString(e));
      }
    })();
  }, [invoiceId, formValues?.isFinanceAnalysisApproved, formValues?.isCxAnalysisApproved]);

  const backToICM = `/credit/${credit.id}?invoices`;
  const backToList = '/cx-invoice';

  if (!invoice) {
    return null;
  }

  return (
    <InitialPage
      title="Invoice validator"
      backTo={backToList} backText="Back to invoices"
      headerRight={<Link className={cls.backBtn} to={backToICM}>Back to ICM</Link>}
      className={cls.wrap}
    >
      <div className={clsx(cls.detail, 'invoice-validator')}>
        <div className={cls.detailCard}>
          <div className={cls.detailCardHead}>
            <div className={cls.detailCardTitle}>Invoice of</div>
            <div className={cls.detailCardDate}>Update {date}</div>
          </div>
          <div className={cls.detailCardContent}>
            <CreditInvoiceUser credit={credit} />
          </div>
        </div>
        <div className={cls.detailCard}>
          <div className={cls.detailCardHead}>
            <div className={cls.detailCardTitle}>External Shop</div>
          </div>
          <div className={cls.detailCardContent}>
            <CreditInvoiceCompany
              retailerName={formValues.retailerName}
              zipState={formValues.zipState}
              xmlUuid={formValues.xmlUuid}
              xmlDate={formValues.xmlDate}
              updateValues={updateValues}
              showErrors={showErrors}
              invoice={invoice}
              submit={submit}
            />
          </div>
        </div>
        <div className={clsx(cls.detailCard, cls.fullWidth)}>
          <div className={cls.detailCardHead}>
            <div className={cls.detailCardTitle}>Invoice Debrief</div>
          </div>
          <div className={cls.detailCardContent}>
            <CreditInvoiceProducts
              invoice={invoice}
              discountAmount={discountAmount}
              setDiscountAmount={setDiscountAmount}
              isrPercent={isrPercent}
              setIsrPercent={setIsrPercent}
              iepsPercent={iepsPercent}
              setIepsPercent={setIepsPercent}
              calculate={calculate}
              composition={composition}
              setComposition={setComposition}
              externalShop={formValues.retailerName}
              submit={submit}
              max={MAX_POSITIONS}
              origin={ProductOriginType.INVOICE}
            />
          </div>
        </div>
        <div className={clsx(cls.detailCard, cls.detailCardNote)}>
          <div className={cls.detailCardHead}>
            <div className={cls.detailCardTitle}>Invoice note</div>
          </div>
          <div className={cls.detailCardContent}>
            {invoice.file && (
              <CreditInvoiceNote
                invoice={invoice}
                creditCode={credit.code}
                file={invoice.file}
                date={commentedDate}
                comment={formValues.comment}
                updateValues={updateValues}
              />
            )}
          </div>
        </div>
        <div className={cls.detailCard}>
          <CreditInvoiceStatus
            currentStatus={formValues.status as CreditInvoiceStatusType}
            updateStatus={updateStatus}
            submit={submit}
            credit={credit}
            calculate={calculate}
            invoice={invoice}
            formValues={formValues}
          />
        </div>
      </div>
    </InitialPage>
  );
};
